import React, { useState, useEffect } from "react";
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle,
  XCircle,
  X,
  Search,
  Edit3,
  Save,
  AlertTriangle,
  Sparkles,
  RefreshCw,
  Building2,
  Hash,
  Calendar,
} from "lucide-react";

const AiDocumentUploader = ({ onClose, onFinalize }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedVendor, setVerifiedVendor] = useState(null);
  const API_PATHS = JSON.parse(localStorage.getItem("apiPaths") || "[]");
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const COMPANY_CODE = localStorage.getItem("companyCode") || "SUNRISEWEB";

  useEffect(() => {
    if (selectedFile) {
      if (selectedFile.type.startsWith("image/")) {
        const url = URL.createObjectURL(selectedFile);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
      } else {
        setPreview(null);
      }
    }
  }, [selectedFile]);

  const handleVendorChange = (field, value) => {
    setResponse({ ...response, [field]: value });
    if (field === "vendorName") {
      setVerifiedVendor(null);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...response.items];
    newItems[index][field] = value;
    setResponse({ ...response, items: newItems });
  };

  const verifySupplier = async (name) => {
    setIsVerifying(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/DupCheck/${COMPANY_CODE}/ACCOUNTPICK/${name}`,
      );
      const data = await res.json();
      setVerifiedVendor(data[0] || {});
    } catch (err) {
      console.error("Verification failed", err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    setError(null);
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      const res = await fetch(
        `${BASE_URL}/api/CRMUploadInvoiceAI/upload-invoice`,
        {
          method: "POST",
          body: formData,
        },
      );
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setResponse(data);
      verifySupplier(data.vendorName);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (response?.items) {
      const newSubtotal = response.items.reduce(
        (sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0),
        0,
      );
      const newNetTotal =
        newSubtotal + (response.taxAmount || 0) - (response.discount || 0);
      setResponse((prev) => ({
        ...prev,
        total: newSubtotal,
        netTotal: newNetTotal,
      }));
    }
  }, [response?.items]);

  const calculateVatPercentage = () => {
    if (!response?.total || !response?.taxAmount) return 0;
    return ((response.taxAmount / response.total) * 100).toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleFinalize = () => {
    if (!response) return;

    // Build the data payload from the form
    const finalizedData = {
      vendorName: response.vendorName,
      vendorAccount: verifiedVendor?.account || "",
      invoiceNumber: response.invoiceNumber,
      invoiceDate: response.invoiceDate,
      items: response.items.map((item) => ({
        code: item.code || "",
        description: item.description,
        quantity: item.quantity || 0,
        unitPrice: item.unitPrice || 0,
        total: (item.quantity || 0) * (item.unitPrice || 0),
      })),
      subtotal: response.total || 0,
      taxAmount: response.taxAmount || 0,
      discount: response.discount || 0,
      netTotal: response.netTotal || 0,
      vatPercentage: calculateVatPercentage(),
    };

    // Call the parent callback to populate PurchaseInvoice
    if (onFinalize && typeof onFinalize === "function") {
      onFinalize(finalizedData);
    }

    // Close the modal
    onClose();
  };

  return (
    <div className="relative bg-white h-full flex flex-col overflow-hidden">
      {!response ? (
        /* --- UPLOAD SECTION --- */
        <div className="p-6 space-y-6 flex-1 flex flex-col justify-center">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-purple-600 text-xs font-bold mb-2">
              <Sparkles className="w-3 h-3" /> AI POWERED
            </div>
            <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
              Document Intelligence
            </h2>
            <p className="text-slate-500 text-sm">
              Fast, accurate data extraction from your PDFs using AI.
            </p>
          </div>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              setSelectedFile(e.dataTransfer.files[0]);
            }}
            className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all duration-300 ${
              isDragging
                ? "border-purple-400 bg-purple-50/50"
                : "border-slate-200 bg-slate-50/50 hover:bg-white hover:border-slate-300"
            }`}
          >
            {!selectedFile ? (
              <label className="cursor-pointer group">
                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white shadow-lg shadow-purple-100 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                  <Upload className="text-purple-600 w-7 h-7" />
                </div>
                <p className="text-slate-700 font-semibold">
                  Drop invoice here
                </p>
                <p className="text-slate-400 text-sm mt-1">
                  or click to browse (PDF)
                </p>
                <input
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                />
              </label>
            ) : (
              <div className="flex flex-col items-center gap-2">
                {preview ? (
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-w-full max-h-40 object-contain rounded-lg shadow-md"
                  />
                ) : (
                  <div className="p-3 bg-white rounded-xl shadow-md">
                    <FileText className="w-8 h-8 text-indigo-500" />
                  </div>
                )}
                <span className="text-slate-900 font-semibold text-sm truncate max-w-xs">
                  {selectedFile.name}
                </span>
                <span className="text-xs text-slate-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setPreview(null);
                  }}
                  className="mt-3 text-xs font-bold text-red-500 uppercase tracking-wider hover:text-red-600"
                >
                  Change File
                </button>
              </div>
            )}
          </div>
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
              <XCircle className="w-4 h-4 text-red-500 shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
          <button
            onClick={handleUpload}
            disabled={!selectedFile || isLoading}
            className="w-full py-3.5 bg-slate-900 text-white rounded-xl font-bold shadow-lg shadow-slate-200 hover:bg-slate-800 transition-all active:scale-[0.98] disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
          >
            {isLoading ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <Sparkles className="w-4 h-4 text-purple-400" />
            )}
            {isLoading ? "Processing with AI..." : "Start AI Extraction"}
          </button>
        </div>
      ) : (
        /* --- RESPONSE SECTION WITH COMPACT LAYOUT --- */
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header Section - More Compact */}
          <div className="p-3 border-b border-slate-100 bg-slate-50/50 space-y-2">
            {/* Vendor Information with inline verification */}
            <div className="p-2.5 rounded-xl bg-white border border-slate-200 shadow-sm">
              <div className="grid grid-cols-2 gap-3">
                {/* Left Side - Vendor Details */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                      Vendor Name
                    </label>

                    {/* Verification Status - Inline */}
                    {isVerifying ? (
                      <div className="flex items-center gap-1 ml-auto">
                        <Loader2 className="w-3 h-3 animate-spin text-indigo-600" />
                        <span className="text-[9px] font-semibold text-indigo-600">
                          Verifying...
                        </span>
                      </div>
                    ) : verifiedVendor?.name ? (
                      <div className="flex items-center gap-1 ml-auto">
                        <CheckCircle className="w-3 h-3 text-emerald-600" />
                        <span className="text-[9px] font-bold text-emerald-700">
                          Verified
                        </span>
                      </div>
                    ) : (
                      <button
                        onClick={() => verifySupplier(response.vendorName)}
                        className="flex items-center gap-1 ml-auto hover:underline"
                      >
                        <Search className="w-3 h-3 text-indigo-600" />
                        <span className="text-[9px] font-bold text-indigo-600">
                          Verify
                        </span>
                      </button>
                    )}
                  </div>

                  <input
                    className="bg-white border border-slate-200 rounded-lg px-2 py-1 w-full text-slate-900 font-semibold text-sm focus:ring-2 focus:ring-purple-200 focus:border-purple-300 outline-none transition-all mb-2 truncate"
                    value={response.vendorName}
                    onChange={(e) =>
                      handleVendorChange("vendorName", e.target.value)
                    }
                  />

                  {/* Verified Account Info */}
                  {verifiedVendor?.account && (
                    <div className="flex items-center gap-2 p-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                      <CheckCircle className="w-3 h-3 text-emerald-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">
                          Account Number
                        </div>
                        <div className="text-xs font-bold text-slate-900 truncate">
                          {verifiedVendor.account}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Side - Invoice Details */}
                <div className="flex flex-col gap-1.5 min-w-0">
                  {/* Invoice Number & Date */}
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="p-1.5 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Hash className="w-3 h-3 text-slate-400" />
                        <label className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">
                          Invoice
                        </label>
                      </div>
                      <div
                        className="text-[11px] font-bold text-slate-900 truncate"
                        title={response.invoiceNumber}
                      >
                        {response.invoiceNumber}
                      </div>
                    </div>

                    <div className="p-1.5 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        <label className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">
                          Date
                        </label>
                      </div>
                      <div className="text-[11px] font-bold text-slate-900">
                        {formatDate(response.invoiceDate)}
                      </div>
                    </div>
                  </div>

                  {/* VAT Rate & Amount */}
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="p-1.5 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="flex items-center gap-1 mb-0.5">
                        <span className="text-[9px] font-black text-blue-500">
                          %
                        </span>
                        <label className="text-[8px] font-bold text-blue-600 uppercase tracking-wider">
                          VAT Rate
                        </label>
                      </div>
                      <div className="text-[11px] font-bold text-slate-900">
                        {calculateVatPercentage()}%
                      </div>
                    </div>

                    <div className="p-1.5 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="flex items-center gap-1 mb-0.5">
                        <span className="text-[9px] font-black text-blue-500">
                          AED
                        </span>
                        <label className="text-[8px] font-bold text-blue-600 uppercase tracking-wider">
                          VAT
                        </label>
                      </div>
                      <div className="text-[11px] font-bold text-slate-900">
                        {response.taxAmount?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Items Table - Scrollable with tighter layout */}
          <div className="flex-1 overflow-auto px-2">
            <table className="w-full text-left border-collapse min-w-max">
              <thead className="bg-slate-100 text-slate-600 text-[9px] font-bold uppercase tracking-wider sticky top-0 z-10">
                <tr>
                  <th className="p-1.5 pl-3 w-10 text-center">#</th>
                  <th className="p-1.5 w-24">Item Code</th>
                  <th className="p-1.5">Description</th>
                  <th className="p-1.5 w-16 text-right">Qty</th>
                  <th className="p-1.5 w-24 text-right">Unit Price</th>
                  <th className="p-1.5 w-24 text-right pr-3">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {response.items.map((item, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-slate-50/50 transition-colors group"
                  >
                    <td className="p-1.5 pl-3 text-center text-[10px] text-slate-400 font-medium">
                      {idx + 1}
                    </td>
                    <td className="p-1.5">
                      <input
                        className="bg-slate-50 border border-slate-200 rounded-md px-1.5 py-1 w-full text-slate-900 font-semibold text-xs focus:ring-2 focus:ring-purple-200 focus:border-purple-300 outline-none transition-all"
                        placeholder="Code"
                        value={item.code || ""}
                        onChange={(e) =>
                          handleItemChange(idx, "code", e.target.value)
                        }
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        className="bg-white border border-slate-200 rounded-md px-1.5 py-1 w-full text-slate-700 text-xs outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(idx, "description", e.target.value)
                        }
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        type="number"
                        className="bg-slate-50 border border-slate-200 rounded-md px-1.5 py-1 w-full text-slate-900 font-semibold text-right text-xs focus:ring-2 focus:ring-purple-200 focus:border-purple-300 outline-none transition-all"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(
                            idx,
                            "quantity",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </td>
                    <td className="p-1.5">
                      <input
                        type="number"
                        step="0.01"
                        className="bg-slate-50 border border-slate-200 rounded-md px-1.5 py-1 w-full text-slate-900 font-semibold text-right text-xs focus:ring-2 focus:ring-purple-200 focus:border-purple-300 outline-none transition-all"
                        value={item.unitPrice}
                        onChange={(e) =>
                          handleItemChange(
                            idx,
                            "unitPrice",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </td>
                    <td className="p-1.5 pr-3 text-right text-slate-900 font-bold text-xs">
                      {(
                        (item.quantity || 0) * (item.unitPrice || 0)
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Footer with Total and Actions */}
          <div className="border-t border-slate-200 bg-white p-3 space-y-2">
            {/* Totals Summary */}
            <div className="space-y-1">
              {/* Subtotal */}
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  Subtotal
                </span>
                <span className="text-sm font-bold text-slate-700">
                  {response.total?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              {/* VAT */}
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  VAT ({calculateVatPercentage()}%)
                </span>
                <span className="text-sm font-bold text-slate-700">
                  {response.taxAmount?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              {/* Discount (if any) */}
              {response.discount > 0 && (
                <div className="flex items-center justify-between px-2 py-1">
                  <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">
                    Discount
                  </span>
                  <span className="text-sm font-bold text-emerald-600">
                    -
                    {response.discount?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              )}
              {/* Grand Total */}
              <div className="flex items-center justify-between px-2 py-2 rounded-lg bg-slate-900 border-2 border-slate-900">
                <span className="text-xs font-bold text-white uppercase tracking-wider">
                  Grand Total
                </span>
                <span className="text-lg font-black text-white">
                  {response.netTotal?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setResponse(null);
                  setVerifiedVendor(null);
                  setSelectedFile(null);
                  setPreview(null);
                }}
                className="flex-1 py-2 px-3 rounded-lg bg-slate-100 text-slate-700 font-semibold text-xs hover:bg-slate-200 transition flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Reset
              </button>
              <button
                onClick={handleFinalize}
                className="flex-[2] py-2 px-3 rounded-lg bg-blue-600 text-white font-semibold text-xs hover:bg-blue-700 transition shadow-md shadow-purple-200 flex items-center justify-center gap-2"
              >
                <Save className="w-3.5 h-3.5" /> Finalize Purchase Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiDocumentUploader;
